import React from 'react'
import { FormattedMessage } from 'react-intl'
import NotificationSettingsProvider, { useNotificationSettings } from 'contexts/notification-settings'
import { useAccess } from 'contexts/access'
import { getCategoryIcon } from 'utilities/categories'
import { Section, Heading, Indenter, Subheading, FieldList, FieldRow, IconMessage, InlineLoaderWrap } from '../s'
import Paragraph from 'components/typography/paragraph'
import Loader from 'components/loader'
import { OrderedList, ListItem } from 'components/list'
import { CheckCircleFilled as Success } from 'components/feather'

const SlackSupportedFeatures = () => {
    const settings = useNotificationSettings()

    const {
        check,
        checkModule,
        checkFeature
    } = useAccess()

    const notifications = settings.notifications
        // Would like to just do this, but awaiting HB-1683:
        // .filter(({ channels }) => 'slack' in (channels ?? {}))

        // Doing this instead for now, supportedNotificationEvents might go out of sync:
        .filter(({ event }) => supportedNotificationEvents.includes(event))

        .map(notification => ({
            ...notification,
            enabled: notification.features
                ?.map(f => f.split(':'))
                .some(([type, name]) => {
                    const checker = (type === 'module') ?
                        checkModule :
                        checkFeature

                    return checker(name)
                }) ?? true
        }))

    const adminNotifications = notifications.filter(({ permissions }) =>
        permissions?.some(permission => check(permission)) ?? false
    )

    const userNotifications = notifications.filter(({ permissions }) => !permissions?.length)

    return (
        <Section>
            <Heading>
                <FormattedMessage
                    id="integration_heading_supported_features"
                    defaultMessage="Supported features" />
            </Heading>
            <OrderedList>
                <ListItem>
                    <IconMessage className="success">
                        <Success size={20} />
                        <div>
                            <Paragraph>
                                <strong>
                                    <FormattedMessage
                                        id="integration_slack_feature_heading_get_notifications"
                                        defaultMessage="Get notifications in Slack for the following events in Huma:" />
                                </strong>
                            </Paragraph>
                        </div>
                    </IconMessage>
                </ListItem>
                <ListItem>
                    {!settings?.hasFetched && (
                        <InlineLoaderWrap>
                            <Loader />
                        </InlineLoaderWrap>
                    )}
                    {!!settings?.hasFetched && (
                        <>
                            {!!adminNotifications.length && (
                                <Indenter>
                                    <Subheading>
                                        <FormattedMessage
                                            id="notification_settings_heading_admin"
                                            defaultMessage="Admin notifications" />
                                    </Subheading>
                                    <FieldList className="indented">
                                        {adminNotifications.map(notification => (
                                            <NotificationMapper
                                                {...notification}
                                                key={`slack:notification:admin:${notification.event}`} />
                                        ))}
                                    </FieldList>
                                </Indenter>
                            )}
                            {!!userNotifications.length && (
                                <Indenter>
                                    <Subheading>
                                        <FormattedMessage
                                            id="notification_settings_heading_user"
                                            defaultMessage="User notifications" />
                                    </Subheading>
                                    <FieldList className="indented">
                                        {userNotifications.map(notification => (
                                            <NotificationMapper
                                                {...notification}
                                                key={`slack:notification:user:${notification.event}`} />
                                        ))}
                                    </FieldList>
                                </Indenter>
                            )}
                        </>
                    )}
                </ListItem>
            </OrderedList>
        </Section>
    )
}

const NotificationMapper = ({ event, category, enabled }) => {
    const Icon = getCategoryIcon(category)

    return (
        <FieldRow
            {...(!enabled ? { className: 'disabled' } : null)}
            key={event}>
            <Icon size={24} />
            <span>
                <FormattedMessage id={`notification_settings_${event}_name`} />
                {!enabled && (
                    <>
                        {' ('}
                        <FormattedMessage
                            id="module_status_disabled"
                            defaultMessage="Disabled" />
                        {')'}
                    </>
                )}
            </span>
        </FieldRow>
    )
}

const supportedNotificationEvents = [
    'roleGranted',
    'salaryUpdated',
    'absenceChangedAdmin',
    'absenceRequestedAdmin',
    'absenceChangedUser',
    'articlePublished',
    'articleActivity',
    'onboardingStarted',
    'onboardingNewHireStartingSoon',
    'onboardingCompleted',
    'documentShared',
    'competenceAdded',
    'competenceAboutToExpire',
    'equipmentAssigned',
    'equipmentAboutToExpire',
    'taskAssigned',
    'taskReminder',
    'surveyRunStarted',
    'surveyReminder',
    'surveyAdmin'
]

export default props => (
    <NotificationSettingsProvider>
        <SlackSupportedFeatures {...props} />
    </NotificationSettingsProvider>
)